import type { CSSProperties } from 'react';
import { EvaluationLevel } from '../../types/enums';
import { levelToLabel, levelToStatusColor } from '../../algorithm/evaluation';

/**
 * StatusBadge - Reusable pill-shaped badge that maps an EvaluationLevel to a
 * color-coded background + label (优秀/良好/中等/偏弱/较差).
 *
 * Colors follow the three-tier status palette:
 *   good (Excellent/Good) → --status-good
 *   ok   (Average)        → --status-ok
 *   bad  (BelowAvg/Poor)  → --status-bad
 */
interface StatusBadgeProps {
  level: EvaluationLevel;
  size?: 'sm' | 'md' | 'lg';
}

type StatusKey = 'good' | 'ok' | 'bad';

const STATUS_COLORS: Record<StatusKey, { text: string; bg: string }> = {
  good: { text: 'var(--status-good)', bg: 'rgba(16, 185, 129, 0.15)' },
  ok: { text: 'var(--status-ok)', bg: 'rgba(245, 158, 11, 0.15)' },
  bad: { text: 'var(--status-bad)', bg: 'rgba(239, 68, 68, 0.15)' },
};

const SIZE_STYLES: Record<'sm' | 'md' | 'lg', CSSProperties> = {
  sm: { fontSize: 'var(--fs-xs)', padding: '1px 8px' },
  md: { fontSize: 'var(--fs-sm)', padding: '2px 10px' },
  lg: { fontSize: 'var(--fs-md)', padding: '4px 14px' },
};

const DOT_SIZE: Record<'sm' | 'md' | 'lg', number> = {
  sm: 5,
  md: 6,
  lg: 8,
};

export default function StatusBadge({ level, size = 'md' }: StatusBadgeProps) {
  const colorKey = levelToStatusColor(level);
  const colors = STATUS_COLORS[colorKey];
  const dot = DOT_SIZE[size];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: 'var(--radius-full)',
        background: colors.bg,
        color: colors.text,
        fontWeight: 'var(--fw-medium)',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        ...SIZE_STYLES[size],
      }}
    >
      <span
        style={{
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: colors.text,
          flexShrink: 0,
        }}
      />
      {levelToLabel(level)}
    </span>
  );
}
